import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/components/prospects/shared";
import { formatTime } from "@/lib/dates";

// Imported (e.g. HubSpot) notes may carry raw HTML markup. Render the readable
// business text only — never execute markup, never discard the note itself.
export function stripHtml(text) {
  if (!text) return text;
  if (!/[<&]/.test(text)) return text;
  const doc = new DOMParser().parseFromString(text, "text/html");
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim() || text;
}

// Display-only normalization of equivalent outcome labels ("Sent email" vs
// "Email sent"). Historical activity text is never overwritten.
const OUTCOME_ALIASES = { "sent email": "Email sent", "email sent": "Email sent", "made call": "Call made", "called": "Call made" };
export function normalizeOutcome(o) {
  if (!o) return o;
  return OUTCOME_ALIASES[o.trim().toLowerCase()] || o;
}
import { Phone, EnvelopeSimple, Buildings, UsersFour, LinkedinLogo, ChatCircle, Handshake, DotsThree, CaretDown, CaretUp, CheckCircle, WarningCircle, Clock } from "@phosphor-icons/react";

const TYPE_ICONS = {
  "Phone Call": Phone, Email: EnvelopeSimple, "Face-to-Face Visit": Buildings, Meeting: UsersFour,
  LinkedIn: LinkedinLogo, "Other Social Media": ChatCircle, "Networking Interaction": Handshake, Other: DotsThree,
};

export const FU_LABELS = {
  overdue: { text: "Overdue", cls: "bg-[#EF4444]/10 text-[#B91C1C]", Icon: WarningCircle },
  due_today: { text: "Due today", cls: "bg-[#F59E0B]/10 text-[#B45309]", Icon: Clock },
  upcoming: { text: "Upcoming", cls: "bg-[#032852]/10 text-[#032852]", Icon: Clock },
  completed: { text: "Completed", cls: "bg-[#569A1F]/10 text-[#457d18]", Icon: CheckCircle },
};

export function FollowUpChip({ status, testId }) {
  const cfg = FU_LABELS[status];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${cfg.cls}`} data-testid={testId}>
      <cfg.Icon size={11} weight="fill" /> {cfg.text}
    </span>
  );
}

// Single timeline entry: header row, expandable details, follow-up actions.
function TimelineItem({ a, showProspect, isOpen, onToggle, isCompleting, note, setNote, onStartComplete, onConfirmComplete, onEdit, onCompleteAndLog, onDelete }) {
  const Icon = TYPE_ICONS[a.activity_type_name] || DotsThree;
  return (
    <div className="rounded-sm border border-[#E4E4E7] bg-white p-4" data-testid={`activity-entry-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-[#032852]/5 text-[#032852]"><Icon size={16} /></span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#09090B]">
              {a.subject || a.activity_type_name}
              <span className="ml-2 rounded-sm bg-[#F4F4F5] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#52525B]">{a.activity_type_name}</span>
            </p>
            <p className="mt-0.5 text-xs text-[#6D6D70]">
              {formatDate(a.activity_date)} · {formatTime(a.activity_time)} · {a.owner_name}
              {showProspect && a.prospect_name ? ` · ${a.prospect_name}` : ""}
              {a.contact_name ? ` · ${a.contact_name}` : ""}
              {a.property_name ? ` · ${a.property_name}` : ""}
            </p>
            {a.outcome && <p className="mt-1 text-xs text-[#52525B]">Outcome: {normalizeOutcome(a.outcome)}</p>}
            {a.follow_up_date && (
              <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#52525B]">
                <FollowUpChip status={a.follow_up_status} testId={`fu-chip-${a.id}`} />
                {a.next_action ? `${a.next_action} — ` : ""}{formatDate(a.follow_up_date)}{a.follow_up_time ? ` ${formatTime(a.follow_up_time)}` : ""}
                {a.follow_up_priority !== "normal" && <span className="font-bold uppercase text-[#B45309]">{a.follow_up_priority}</span>}
              </p>
            )}
          </div>
        </div>
        <button onClick={onToggle} className="p-1 text-[#6D6D70] hover:text-[#032852]" data-testid={`expand-activity-${a.id}`}>
          {isOpen ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </button>
      </div>
      {isOpen && (
        <div className="mt-3 border-t border-[#F4F4F5] pt-3 text-sm text-[#52525B]" data-testid={`activity-details-${a.id}`}>
          {a.notes ? <p className="whitespace-pre-wrap">{stripHtml(a.notes)}</p> : <p className="text-xs text-[#6D6D70]">No detailed notes.</p>}
          {a.follow_up_completed && (
            <p className="mt-2 text-xs text-[#457d18]">
              Follow-up completed {formatDate(a.follow_up_completed_at)}{a.follow_up_completion_note ? ` — ${a.follow_up_completion_note}` : ""}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {onEdit && <button className="font-semibold text-[#032852] hover:text-[#569A1F]" onClick={() => onEdit(a)} data-testid={`edit-activity-${a.id}`}>Edit</button>}
            {a.follow_up_date && !a.follow_up_completed && (
              <>
                <button className="font-semibold text-[#457d18] hover:text-[#569A1F]" onClick={onStartComplete} data-testid={`complete-fu-${a.id}`}>Complete follow-up</button>
                {onCompleteAndLog && (
                  <button className="font-semibold text-[#032852] hover:text-[#569A1F]" onClick={() => onCompleteAndLog(a)} data-testid={`complete-and-log-${a.id}`}>Complete & log new activity</button>
                )}
              </>
            )}
            {onDelete && (
              <button className="ml-auto font-semibold text-[#B91C1C] hover:text-[#EF4444]" onClick={() => onDelete(a)} data-testid={`delete-activity-${a.id}`}>Delete</button>
            )}
          </div>
          {isCompleting && (
            <div className="mt-2 flex items-center gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Completion note (optional)" className="rounded-sm text-xs" data-testid="completion-note" />
              <Button size="sm" onClick={onConfirmComplete} className="rounded-sm bg-[#569A1F] text-xs font-bold text-white hover:bg-[#457d18]" data-testid="confirm-complete">Complete</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Chronological timeline (newest first), expandable entries, follow-up actions.
export default function ActivityTimeline({ activities, showProspect, onCompleteAndLog, onEdit, onDone }) {
  const { user, isAdmin } = useAuth();
  const [expanded, setExpanded] = useState(null);
  const [completing, setCompleting] = useState(null); // activity id with note input
  const [note, setNote] = useState("");

  // Permanent deletion: activity owner or administrator only (backend-enforced
  // too). Requires explicit confirmation; the audit entry survives.
  const canDelete = (a) => isAdmin || a.owner_id === user?.id;
  const deleteActivity = async (a) => {
    if (!window.confirm("Permanently delete this activity? This cannot be undone — an audit-trail entry is recorded.")) return;
    try {
      await api.delete(`/activities/${a.id}`);
      toast.success("Activity permanently deleted");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const completeOnly = async (a) => {
    if (!note.trim()) {
      toast.error("A completion outcome/conclusion is required to finalize this follow-up");
      return;
    }
    try {
      await api.post(`/activities/${a.id}/complete-follow-up`, { note: note.trim() });
      toast.success("Follow-up completed");
      setCompleting(null);
      setNote("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!activities.length) {
    return <p className="rounded-sm border border-dashed border-[#E4E4E7] bg-white py-10 text-center text-sm text-[#6D6D70]">No activities logged yet.</p>;
  }
  return (
    <div className="space-y-2">
      {activities.map((a) => (
        <TimelineItem
          key={a.id}
          a={a}
          showProspect={showProspect}
          isOpen={expanded === a.id}
          onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
          isCompleting={completing === a.id}
          note={note}
          setNote={setNote}
          onStartComplete={() => { setCompleting(a.id); setNote(""); }}
          onConfirmComplete={() => completeOnly(a)}
          onEdit={onEdit}
          onCompleteAndLog={onCompleteAndLog}
          onDelete={canDelete(a) ? deleteActivity : null}
        />
      ))}
    </div>
  );
}
